import * as React from 'react'

import { Link } from '@factorialco/deprecated-design-system'
import cn from 'classnames/bind'
import type { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import DocumentNavigatorCompatible from 'modules/documents/components/DocumentNavigator/Compatible'
import type { NavigatorContentProps } from 'modules/documents/components/DocumentPreview'
import DocumentPreview from 'modules/documents/components/DocumentPreview'
import DocumentUploader from 'modules/documents/components/Documents/DocumentUploader'
import type DocumentModel from 'modules/documents/store/models/Document'

import styles from './index.module.scss'

interface Props {
  field: Field
  document: DocumentModel | null | undefined
  readOnly?: boolean
  onDeleteDocument?: () => void
  placeholder?: string
}

const cx = cn.bind(styles)

const DocumentUploaderWithLinks: React.FC<Props> = (props) => {
  const { field, document, readOnly, onDeleteDocument: onDeleteDocumentProp, placeholder } = props

  const [preview, setPreview] = React.useState(false)

  const onDeleteDocument = async () => {
    if (readOnly || !document) return
    await asyncAction(() => document.destroy())
    field.set(null)
    if (onDeleteDocumentProp) {
      onDeleteDocumentProp()
    }
  }

  const onPreviewDocument = React.useCallback(() => {
    setPreview(true)
  }, [])

  const onClosePreview = React.useCallback(() => {
    setPreview(false)
  }, [])

  const stopPropagation = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
  }, [])

  const renderPreview = React.useCallback(
    (navProps: NavigatorContentProps) => <DocumentPreview {...navProps} />,
    []
  )

  const renderNavigator = () => {
    if (!preview || !document) return null
    return (
      <DocumentNavigatorCompatible
        documentIds={[document.id]}
        index={0}
        onClose={onClosePreview}
        content={renderPreview}
      />
    )
  }

  const renderDocumentLinks = () => {
    if (!document) return null
    return (
      <div className={cx(styles.documentLinks, { readOnly })} onClick={stopPropagation}>
        <div className={styles.item}>
          <Link.Touchable onClick={onPreviewDocument}>
            {i18n.t('common.view_document')}
          </Link.Touchable>
        </div>
        {!readOnly && (
          <div className={styles.item}>
            <Link.Touchable onClick={onDeleteDocument}>{i18n.t('common.remove')}</Link.Touchable>
          </div>
        )}
      </div>
    )
  }

  const renderUploader = () => (
    <DocumentUploader
      field={field}
      placeholder={placeholder || ''}
      readonly={readOnly}
      renderChildren={renderDocumentLinks}
    />
  )

  if (!document) {
    return renderUploader()
  }

  return (
    <>
      {renderNavigator()}
      {renderUploader()}
    </>
  )
}

export default observer(DocumentUploaderWithLinks)
